import React, { useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';

import { UpwardAdvancedLoader } from 'ui';
import ErrorAlertModal from '../app/employer/components/withErrorAlertModal/ErrorAlertModal';
import Wrapper from '../components/Wrapper';
import Main from '../components/Main';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SideNav from '../components/SideNav';
import DirectEmployerDeprecationBanner from '../components/DirectEmployerDeprecationBanner';
import PartnerSessionExpiredBanner from '../components/PartnerSessionExpiredBanner';
import authRouteList from '../routes/authRoutes';
import { isDirectEmployerPagePath } from '../constants/deprecation';
import UpwardOverlayLoader from '../ui/loaders/UpwardOverlayLoader';

const authRouteListPath = authRouteList.map(({ path }) => path);

/**
 * Dashboard component.
 *
 * @param {Object} props - The component props.
 * @param {ReactNode} props.children - The child components.
 * @param {boolean} props.isLoggedIn - Indicates if the user is logged in.
 * @param {boolean} props.isLoading - Indicates if the component is in a loading state.
 * @param {boolean} props.isLoggedInPartner - Indicates if the user is a partner and logged in.
 *
 * @returns {ReactNode} The rendered Dashboard component.
 */
const Dashboard = ({ children, isLoggedIn, isLoading, isLoggedInPartner }) => {
   const [isUpdatingServiceWorker, setIsUpdatingServiceWorker] = useState(false);
   const [errorMessage] = useState(false);

   const { pathname } = useLocation();
   const isFullPage = authRouteListPath.includes(pathname);


   const isServiceWorkerUpdated = useSelector(
      (state) => state.serviceWorker.serviceWorkerUpdated,
   );

   const serviceWorkerRegistration = useSelector(
      (state) => state.serviceWorker.serviceWorkerRegistration,
   );

   const updateServiceWorker = () => {
      const registrationWaiting = serviceWorkerRegistration.waiting;
      if (registrationWaiting) {
         setIsUpdatingServiceWorker(true);
         registrationWaiting.postMessage({ type: 'SKIP_WAITING' });
         registrationWaiting.addEventListener('statechange', (e) => {
            if (e.target.state === 'activated') {
               window.location.reload();
            }
         });
      }
   };

   return (
      <>
         <Wrapper>
            {
               isLoading
               && <UpwardOverlayLoader/>
            }
            {
               isLoggedIn && !isFullPage
               && (
                  <>
                     <Navbar/>
                     <SideNav/>
                  </>
               )
            }
            {
               errorMessage
                  && (
                     <ErrorAlertModal
                        isOpen
                        error={errorMessage}
                     />
                  )
            }
            <Main>
               <div className={`content ${ isLoading ? 'd-none' : '' }`}>
                  {
                     isDirectEmployerPagePath(pathname)
                     && <DirectEmployerDeprecationBanner/>
                  }
                  <PartnerSessionExpiredBanner/>
                  {children}
               </div>
               {
                  isLoggedIn && !isFullPage
                  && <Footer/>
               }
            </Main>
         </Wrapper>
         <Modal isOpen={isServiceWorkerUpdated}>
            <ModalHeader>New version of app available</ModalHeader>
            <ModalBody>
               {!isUpdatingServiceWorker && (
                  <>
                     <p>
                        There is new version available. Please&nbsp;
                        <a
                           href='#'
                           onclick='window.location.reload();'
                        >
                           reload&nbsp;
                        </a>
                        to get the latest version.
                     </p>
                     <div className='text-center'>
                        <button
                           className='btn btn-rounded btn-dark-orange'
                           onClick={ updateServiceWorker }
                        >
                           Update
                        </button>
                     </div>
                  </>
               )}
               {
                  isUpdatingServiceWorker
                  && <UpwardAdvancedLoader size='120'/>
               }
            </ModalBody>
         </Modal>
      </>
   );
};

const mapStateToProps = (state) => {
   const isLoggedInEmployer = !!(state.authReducer?.isLoggedIn);
   const isLoggedInPartner = !!(state.authReducer?.isLoggedInPartner);
   return {
      isLoggedIn: isLoggedInEmployer || isLoggedInPartner,
      isLoading: state.authReducer.global.isLoading,
      isLoggedInPartner,
   };
};

export default connect(
   mapStateToProps,
)(Dashboard);
